import { stringify } from 'query-string';
import axios from './utils/axios';
import { eventLogUpdate, cleanupFormValues } from '~/utils';
import { store } from './App';
import { configurationUpdate, solutionUpdate } from '~/ducks/solutions';
import withLocalFilters from '~/utils/withLocalFilters';
import { LEDGERS_MAMBU } from '~/utils/constants/resources';
import { removeDraftPostfix } from '~/hooks/draft';

export const handleFiles = (resource, json) => {
  const tasks = [];
  const scan = (obj) => {
    Object.entries(obj).forEach(([key, value]) => {
      if (!value) {
        return;
      }
      if (value.rawFile instanceof File) {
        tasks.push({
          obj,
          key,
          file: value.rawFile,
          anonymous: value.anonymous,
        });
      } else if (Array.isArray(value)) {
        value.forEach((item, i) => {
          if (item.rawFile instanceof File) {
            tasks.push({
              arr: value,
              index: i,
              file: item.rawFile,
              anonymous: item.anonymous,
            });
          } else {
            if (typeof item === 'object') {
              scan(item);
            }
          }
        });
      } else if (typeof value === 'object') {
        scan(value);
      }
    });
  };
  scan(json);

  const upload = (task) => {
    const formData = new FormData();
    formData.append('data', task.file);
    if (resource === 'individuals') {
      formData.append('profileId', removeDraftPostfix(json.id));
      formData.append('isPublic', true);
    }
    if (resource === 'businesses' || resource === 'locations') {
      formData.append('isPublic', true);
    }
    if (task.anonymous) {
      formData.append('anonymous', true);
    }
    return axios({
      method: 'post',
      url: resource.startsWith('paymentServices')
        ? '/paymentServices/icons'
        : resource.startsWith('menu')
        ? 'menu/icons'
        : '/files',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then((response) => {
        if (task.arr) {
          task.arr[task.index] = {
            id: response.data.id,
            name: task.file.name,
            mimeType: task.file.type || task.file.mimeType,
            size: task.file.size,
          };
        } else if (task.obj) {
          task.obj[task.key] = {
            id: response.data.id,
            name: task.file.name,
            mimeType: task.file.type || task.file.mimeType,
            size: task.file.size,
          };
        }
      })
      .catch(() => {
        // TODO add logging
        task.obj[task.key] = null;
      });
  };

  const uploadPromise = Promise.all(
    tasks.map((task) => {
      return upload(task);
    })
  );

  return uploadPromise;
};

const withFiles = (resource, params) => {
  return (f) => {
    if (params.data) {
      console.log(params.data);
      return handleFiles(resource, params.data).then(() => {
        return f();
      });
    } else {
      return f();
    }
  };
};

const addMimeTypeToPaymentIcon = (resource) => (item) => {
  if (
    (resource === 'paymentServices' || resource === 'menu') &&
    typeof item.branding?.icon === 'string'
  ) {
    return {
      ...item,
      ...{
        branding: {
          color: item.branding.color,
          icon: {
            id: item.branding.icon,
            mimeType: 'image',
          },
        },
      },
    };
  }
  return item;
};

const assignId = (item) => {
  if (!item.id && item.key) {
    return Object.assign(item, { id: item.key });
  } else {
    return item;
  }
};

const injectEncodedKey = (data) => {
  if (!data.identificationDetails) {
    data.identificationDetails = {};
  }
  if (!data.identificationDetails.referenceId) {
    data.identificationDetails.referenceId = [];
  }
  if (typeof data.identificationDetails.details === 'string') {
    data.identificationDetails.details = JSON.parse(data.identificationDetails.details);
  }
  if (data.encodedKey) {
    if (!data.identificationDetails.details) {
      data.identificationDetails.details = {};
    }
    data.identificationDetails.details.encodedKey = data.encodedKey;
    delete data.encodedKey;
  }
  return data;
};

export const extractEncodedKey = (data) => {
  if (data.identificationDetails?.details) {
    const details =
      typeof data.identificationDetails.details === 'string'
        ? JSON.parse(data.identificationDetails.details)
        : data.identificationDetails.details;

    if (details.encodedKey) {
      data.encodedKey = details.encodedKey;
      delete details.encodedKey;
    }
    data.identificationDetails.details = JSON.stringify(details);
  }
  return data;
};

const dataProvider = {
  getList: withLocalFilters(async (resource, params) => {
    // do not query individuals if filter is not set
    if (resource === 'individuals' && params.filter) {
      const { phoneNumber } = params.filter;
      const extendedSearch = params.filter.extendedSearch;
      if (
        !extendedSearch &&
        (Object.keys(params.filter).length === 0 || (phoneNumber && phoneNumber.length <= 6))
      ) {
        return Promise.resolve({ data: [], total: 0, noFilter: true });
      }
    }

    const { page, perPage } = params.pagination;
    const query = {
      page,
      perPage,
      ...(params.filter || {}),
      ...(params.sort || {}),
    };
    let url = '';
    if (query.solutionId && resource !== 'fxrates/mappings' && resource !== 'menu/mappings') {
      const { solutionId } = query;
      delete query.solutionId;
      url = `/solutions/${solutionId}/${resource}?${stringify(query)}`;
    } else if (query.businessId && resource !== 'fxrates/mappings' && resource !== 'employees') {
      const { businessId } = query;
      delete query.businessId;
      url = `/businesses/${businessId}/${resource}?${stringify(query)}`;
    } else {
      url = `/${resource}?${stringify(query)}`;
    }
    const response = await axios({ method: 'get', url });
    if (!response.headers['content-range']) {
      if (resource !== 'fxrates' && resource !== 'accounts' && resource !== 'beneficiaries') {
        throw new Error(
          'The Content-Range header is missing in the HTTP Response. If you are using CORS, did you declare Content-Range in the Access-Control-Expose-Headers header?'
        );
      }
    }
    return {
      data: response.data.map(assignId).map(addMimeTypeToPaymentIcon(resource)),
      total: response.headers['content-range']
        ? parseInt(response.headers['content-range'].split('/').pop(), 10)
        : 9999,
    };
  }),
  getOne: async (resource, params) => {
    if (resource === LEDGERS_MAMBU) {
      return { data: { id: LEDGERS_MAMBU } };
    }
    const url = `/${resource}/${params.id}`;
    const response = await axios({ method: 'get', url });
    // Handle externalAccountLinks encodedKey
    const data =
      resource === 'externalAccountLinks' ? extractEncodedKey(response.data) : response.data;
    return {
      data: addMimeTypeToPaymentIcon(resource)(data),
    };
  },
  getMany: withLocalFilters(async (resource, params) => {
    const query = {
      ids: params.ids,
      ...(params.filter || {}),
      ...(params.sort || {}),
    };
    let url = '';
    if (query.businessId && resource !== 'employees') {
      const { businessId } = query;
      delete query.businessId;
      url = `/businesses/${businessId}/${resource}?${stringify(query)}`;
    } else {
      url = `/${resource}?${stringify(query)}`;
    }
    const response = await axios({ method: 'get', url });
    return {
      data: response.data.map(assignId).map(addMimeTypeToPaymentIcon(resource)),
    };
  }),
  getManyReference: withLocalFilters(async (resource, params) => {
    const {
      filter = {},
      pagination: { page, perPage },
    } = params;
    const query = { page, perPage, ...cleanupFormValues(filter), ...(params.sort || {}) };
    let url = `/${params.target}/${params.id}/${resource}?${stringify(query)}`;
    if (resource === 'fxrates/mappings' || resource === 'menu/mappings') {
      url = `/${resource}?solutionId=${params.id}&${stringify(query)}`;
    }
    const response = await axios({ method: 'get', url });
    if (!response.headers['content-range']) {
      throw new Error(
        'The Content-Range header is missing in the HTTP Response. If you are using CORS, did you declare Content-Range in the Access-Control-Expose-Headers header?'
      );
    }
    return {
      data: response.data.map(assignId).map(addMimeTypeToPaymentIcon(resource)),
      total: parseInt(response.headers['content-range'].split('/').pop(), 10),
    };
  }),
  create: (resource, params) => {
    return withFiles(
      resource,
      params
    )(async () => {
      let url = params.subresource
        ? `/${resource}/${params.id}/${params.subresource}` // Handle nested resources
        : `/${resource}`; // Default case for non-nested resources;
      if (
        !params.subresource &&
        (resource === 'individuals' || resource === 'businesses' || resource === 'pricings')
      ) {
        url = `${url}/drafts`;
      }
      if (resource === 'businesses') {
        params.data.email = params.data.employee?.email;
      }
      if (resource === 'paymentServices') {
        [
          'parameterGroups',
          'parameters',
          'amount',
          'senderAmount',
          'deal',
          'i18n',
          'tags',
          'additionalProperties',
          'recipientExternalSource',
        ].forEach((key) => {
          if (typeof params.data[key] === 'string') {
            params.data[key] = JSON.parse(params.data[key]);
          }
        });
        if (typeof params.data?.processing?.parameters === 'string') {
          params.data.processing.parameters = JSON.parse(params.data.processing.parameters);
        }
        if (typeof params.data?.amount?.single === 'string') {
          params.data.amount.single = JSON.parse(params.data.amount.single);
        }
        if (typeof params.data?.amount?.beneficiary === 'string') {
          params.data.amount.beneficiary = JSON.parse(params.data.amount.beneficiary);
        }
        if (typeof params.data?.amount?.remitter === 'string') {
          params.data.amount.remitter = JSON.parse(params.data.amount.remitter);
        }
        if (params.data.beneficiary?.selection === 'DISABLED') {
          delete params.data?.beneficiary?.allowedCountries;
          delete params.data?.beneficiary?.allowedPhoneNumberPrefixes;
          delete params.data?.beneficiary?.allowedTypes;
        } else if (params.data?.beneficiaryConfigurationEnabled === false) {
          delete params.data?.beneficiary;
        }
        if (params.data?.beneficiary?.allowedPhoneNumberPrefixes?.length === 0) {
          delete params.data?.beneficiary?.allowedPhoneNumberPrefixes;
        }
        if (params.data?.beneficiary?.allowedCountries?.length === 0) {
          delete params.data?.beneficiary?.allowedCountries;
        }
        delete params.data?.beneficiaryConfigurationEnabled;

        if (params.data.amount?.mode === 'BENEFICIARY_REMITTER') {
          delete params.data?.amount?.single;
        } else if (
          params.data.amount?.mode === 'FIXED' ||
          params.data.amount?.mode === 'SUGGESTED' ||
          params.data.amount?.mode === 'DEFAULT'
        ) {
          delete params.data?.amount?.beneficiary;
          delete params.data?.amount?.remitter;
        }
        if (params.data.amountConfigurationEnabled === false) {
          delete params.data?.amount;
        }
        delete params.data?.amountConfigurationEnabled;
      }
      if (resource === 'pricings' || resource === 'paymentInstrumentAvailabilityRules') {
        delete params.data.paymentInstrument;
      }
      // Handle externalAccountLinks encodedKey
      if (resource === 'externalAccountLinks') {
        params.data = injectEncodedKey(params.data);
      }
      const response = await axios.post(url, params.data);
      eventLogUpdate(resource);
      return { data: { ...params.data, ...response.data } };
    });
  },
  update: (resource, params) => {
    return withFiles(
      resource,
      params
    )(async () => {
      if (resource === 'externalSources' && params.subresource === 'names') {
        const name = params.data.name;
        const alias = params.data.alias;
        params.data = { name: name ?? '', alias: alias };
      }
      if (
        (resource === 'accounts' || resource === 'tenant/accounts') &&
        params.subresource === 'labels'
      ) {
        const { name, alias, description } = params.data;
        params.data = { name: name ?? '', alias: alias ?? '', description: description ?? '' };
      }
      const { data, id, subresource, HTTPMethod = 'PUT' } = params;
      const nestedChildId = subresource === 'legalConsents' ? data?.id : undefined;
      const url = subresource
        ? `/${resource}/${id}/${subresource}${nestedChildId ? `/${nestedChildId}` : ''}`
        : `/${resource}/${id}`;
      const headers = {};
      if (typeof data === 'string') headers['Content-Type'] = 'text/plain';

      if (resource === 'paymentServices') {
        [
          'parameterGroups',
          'parameters',
          'amount',
          'senderAmount',
          'deal',
          'i18n',
          'tags',
          'additionalProperties',
          'recipientExternalSource',
        ].forEach((key) => {
          if (typeof params.data[key] === 'string') {
            params.data[key] = JSON.parse(params.data[key]);
          }
        });
        if (typeof params.data?.processing?.parameters === 'string') {
          params.data.processing.parameters = JSON.parse(params.data.processing.parameters);
        }
        if (typeof params.data?.amount?.single === 'string') {
          params.data.amount.single = JSON.parse(params.data.amount.single);
        }
        if (typeof params.data?.amount?.beneficiary === 'string') {
          params.data.amount.beneficiary = JSON.parse(params.data.amount.beneficiary);
        }
        if (typeof params.data?.amount?.remitter === 'string') {
          params.data.amount.remitter = JSON.parse(params.data.amount.remitter);
        }

        if (params.data.beneficiary?.selection === 'DISABLED') {
          delete params.data?.beneficiary?.allowedCountries;
          delete params.data?.beneficiary?.allowedPhoneNumberPrefixes;
          delete params.data?.beneficiary?.allowedTypes;
        } else if (params.data?.beneficiaryConfigurationEnabled === false) {
          delete params.data?.beneficiary;
        }
        if (params.data?.beneficiary?.allowedPhoneNumberPrefixes?.length === 0) {
          delete params.data?.beneficiary?.allowedPhoneNumberPrefixes;
        }
        if (params.data?.beneficiary?.allowedCountries?.length === 0) {
          delete params.data?.beneficiary?.allowedCountries;
        }
        delete params.data?.beneficiaryConfigurationEnabled;

        if (params.data.amount?.mode === 'BENEFICIARY_REMITTER') {
          delete params.data?.amount?.single;
        } else if (
          params.data.amount?.mode === 'FIXED' ||
          params.data.amount?.mode === 'SUGGESTED' ||
          params.data.amount?.mode === 'DEFAULT'
        ) {
          delete params.data?.amount?.beneficiary;
          delete params.data?.amount?.remitter;
        }
        if (params.data.amountConfigurationEnabled === false) {
          delete params.data?.amount;
        }
        delete params.data?.amountConfigurationEnabled;
      } else if (resource === 'externalProfileLinks') {
        ['identificationDetails'].forEach((key) => {
          if (typeof params.data[key] === 'string') {
            params.data[key] = JSON.parse(params.data[key]);
          }
        });
      } else if (resource === 'solutions') {
        if (typeof params.data?.veengu_individual_remittance?.processing?.parameters === 'string') {
          params.data.veengu_individual_remittance.processing.parameters = JSON.parse(
            params.data.veengu_individual_remittance.processing.parameters
          );
        }
        if (
          typeof params.data?.veengu_partner_incoming_transfer_debit?.processing?.parameters ===
          'string'
        ) {
          params.data.veengu_partner_incoming_transfer_debit.processing.parameters = JSON.parse(
            params.data.veengu_partner_incoming_transfer_debit.processing.parameters
          );
        }
        if (typeof params.data?.veengu_individual_card_issuing?.cardsConfiguration === 'string') {
          params.data.veengu_individual_card_issuing.cardsConfiguration = JSON.parse(
            params.data.veengu_individual_card_issuing.cardsConfiguration
          );
        }
        if (
          typeof params.data?.veengu_business_remittance_payout?.fastPayoutProfileSearchRules ===
          'string'
        ) {
          params.data.veengu_business_remittance_payout.fastPayoutProfileSearchRules = JSON.parse(
            params.data.veengu_business_remittance_payout.fastPayoutProfileSearchRules
          );
        }
        if (
          typeof params.data?.veengu_partner_remittance_payout?.fastPayoutProfileSearchRules ===
          'string'
        ) {
          params.data.veengu_partner_remittance_payout.fastPayoutProfileSearchRules = JSON.parse(
            params.data.veengu_partner_remittance_payout.fastPayoutProfileSearchRules
          );
        }
        if (
          typeof params.data?.veengu_individual_authentication?.secretWordSelfReset?.questions ===
          'string'
        ) {
          params.data.veengu_individual_authentication.secretWordSelfReset.questions = JSON.parse(
            params.data.veengu_individual_authentication.secretWordSelfReset.questions
          );
        }
        if (
          typeof params.data?.veengu_individual_profile?.nonAccountBasedPaymentInstruments ===
          'string'
        ) {
          params.data.veengu_individual_profile.nonAccountBasedPaymentInstruments = JSON.parse(
            params.data.veengu_individual_profile.nonAccountBasedPaymentInstruments
          );
        }
      }
      if (resource === 'pricings' || resource === 'paymentInstrumentAvailabilityRules') {
        const paymentInstrument = params.data.paymentInstrument;
        if (paymentInstrument === 'product') {
          delete params.data.externalSourceType;
        }
        if (paymentInstrument === 'externalSourceType') {
          delete params.data.productId;
        }
        delete params.data.paymentInstrument;
      }

      let brandingIcon = undefined;
      if (resource.startsWith('paymentServices') && params.data.branding?.icon) {
        brandingIcon = params.data.branding.icon;
        params.data.branding.icon = params.data.branding.icon.id;
      }

      // Clean up menu items before submitting
      if (resource.startsWith('menu') && params.data.menuId) {
        if (HTTPMethod === 'PUT') {
          if (params.data.branding?.icon) {
            brandingIcon = params.data.branding.icon;
            params.data.branding.icon = params.data.branding.icon.id;
          }
          if (params.data.parentId === params.data.menuId) {
            delete params.data.parentId;
          }
          delete params.data.children;
          delete params.data.isLoading;
          delete params.data.type;
          delete params.data.name;
          if (params.data.itemType === 'GROUP') {
            delete params.data.serviceId;
            delete params.data.actionId;
          } else if (params.data.itemType === 'ACTION') {
            delete params.data.serviceId;
          } else if (params.data.itemType === 'SERVICE') {
            delete params.data.actionId;
          }
        }
      }

      if (resource === 'externalAccountLinks') {
        params.data = injectEncodedKey(params.data);
      }

      let response = {};
      if (HTTPMethod === 'PUT') {
        response = await axios.put(url, data, { headers });
      } else if (HTTPMethod === 'POST') {
        response = await axios.post(url, data, { headers });
      }
      eventLogUpdate(resource);
      if (resource === 'solutions') {
        if (subresource === 'configuration') {
          store.dispatch(
            configurationUpdate({
              solutionId: id,
              data,
            })
          );
        } else if (!subresource) {
          store.dispatch(
            solutionUpdate({
              solutionId: id,
              data,
            })
          );
        }
      }
      return {
        data: {
          id: '',
          ...data,
          ...response.data,
          ...(brandingIcon
            ? { branding: { color: response.data.branding.color, icon: brandingIcon } }
            : {}),
        },
      };
    });
  },
  updateMany: () => Promise,
  delete: async (resource, params) => {
    let url = `/${resource}/${params.id}`;

    if (params.subresource) {
      const parentId = params.parentId || params.id;
      const nestedChildId = params.resourceId || params.id;
      url = `/${resource}/${parentId}/${params.subresource}/${nestedChildId}`;
    }

    await axios.delete(url);
    eventLogUpdate(resource);
    return { data: { id: params.resourceId || params.id } };
  },
  deleteMany: async (resource, params) => {
    const query = { uid: params.ids };
    const url = `/${resource}?${stringify(query)}`;
    await axios.delete(url);
    eventLogUpdate(resource);
    return { data: params.ids };
  },
  // custom
  getResource: async (resource, params) => {
    const parameters = params || resource;
    const url = `/${parameters.url}`;
    const response = await axios.get(url);
    return { data: response.data };
  },
  getSolutionConfigurationSchema: async (resource, params) => {
    const url = `/solutions/${params.id}/${params.resource}/schema`;
    const response = await axios.get(url);
    return { data: { configurationSchema: response.data } };
  },
  getSolutionHandbook: async (resource, params) => {
    const url = `/solutions/${params.solutionId}/handbook`;
    const response = await axios.get(url);
    return { data: response.data };
  },
  updateSolutionHandbook: async (resource, params) => {
    const url = `/solutions/${params.solutionId}/handbook`;
    await axios.put(url, params.handbook);
    return { data: params.handbook };
  },
};

export default dataProvider;
